/**
 * Cloudflare Key-Value API Integration via Proxy
 * Provides functionality to read from and write to Cloudflare KV storage
 * Using a proxy server to avoid CORS issues
 */

// Types for Cloudflare KV API
interface CloudflareApiConfig {
  accountId: string;
  namespaceId: string;
  apiToken: string;
  proxyUrl: string;
}

interface CloudflareKVResponse {
  success: boolean;
  errors: any[];
  messages: any[];
  result: any;
}

// Configuration for the Cloudflare KV API
let apiConfig: CloudflareApiConfig | null = null;

// Debug flag - set to true to enable detailed logging
const DEBUG = true;

/**
 * Initialize the Cloudflare KV API with configuration
 */
export const initCloudflareApi = (config: CloudflareApiConfig): void => {
  if (DEBUG)
    console.log("🔧 Initializing Cloudflare API with config:", {
      accountId: config.accountId
        ? `${config.accountId.substring(0, 4)}...`
        : "missing",
      namespaceId: config.namespaceId
        ? `${config.namespaceId.substring(0, 4)}...`
        : "missing",
      apiToken: config.apiToken ? "present" : "missing",
      proxyUrl: config.proxyUrl,
    });

  apiConfig = config;
};

/**
 * Get configuration status
 */
export const isConfigured = (): boolean => {
  const configured = apiConfig !== null;
  if (DEBUG) console.log(`🔍 Cloudflare API configured: ${configured}`);
  return configured;
};

/**
 * Get configuration
 */
export const getConfig = (): CloudflareApiConfig | null => {
  if (DEBUG && !apiConfig)
    console.warn("⚠️ Attempted to get config but API is not configured");
  return apiConfig;
};

/**
 * Check if the proxy server is available
 */
export const checkProxyConnection = async (): Promise<boolean> => {
  if (!apiConfig) {
    console.error("❌ Proxy check failed: API not configured");
    return false;
  }

  try {
    console.log("🔄 Checking proxy server connection...");
    const response = await fetch(`${apiConfig.proxyUrl}/api/health`);

    if (!response.ok) {
      console.error(
        `❌ Proxy server health check failed: ${response.status} ${response.statusText}`
      );
      return false;
    }

    const data = await response.json();
    console.log("✅ Proxy server is available:", data);
    return true;
  } catch (error) {
    console.error("❌ Proxy server connection failed:", error);
    return false;
  }
};

/**
 * Set a key-value pair in Cloudflare KV storage
 */
export const setKVValue = async (key: string, value: any): Promise<boolean> => {
  if (!apiConfig) {
    console.error(
      "❌ Cloudflare API not configured. Call initCloudflareApi first."
    );
    throw new Error(
      "Cloudflare API not configured. Call initCloudflareApi first."
    );
  }

  if (DEBUG) console.log(`🔄 Setting KV value for key "${key}"`);

  try {
    const { accountId, namespaceId, proxyUrl } = apiConfig;
    // Use our proxy instead of direct Cloudflare API
    const endpoint = `${proxyUrl}/api/cloudflare/accounts/${accountId}/storage/kv/namespaces/${namespaceId}/values/${key}`;

    if (DEBUG) console.log(`🔄 PUT request to: ${endpoint}`);

    const response = await fetch(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(value),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error(`❌ Error setting KV value for key "${key}":`, errorData);
      console.error(`Status: ${response.status} ${response.statusText}`);
      return false;
    }

    if (DEBUG) console.log(`✅ Successfully set KV value for key "${key}"`);
    return true;
  } catch (error) {
    console.error(`❌ Error setting KV value for key "${key}":`, error);
    return false;
  }
};

/**
 * Get a value from Cloudflare KV storage by key
 */
export const getKVValue = async <T>(key: string): Promise<T | null> => {
  if (!apiConfig) {
    console.error(
      "❌ Cloudflare API not configured. Call initCloudflareApi first."
    );
    throw new Error(
      "Cloudflare API not configured. Call initCloudflareApi first."
    );
  }

  if (DEBUG) console.log(`🔄 Getting KV value for key "${key}"`);

  try {
    const { accountId, namespaceId, proxyUrl } = apiConfig;
    // Use our proxy instead of direct Cloudflare API
    const endpoint = `${proxyUrl}/api/cloudflare/accounts/${accountId}/storage/kv/namespaces/${namespaceId}/values/${key}`;

    if (DEBUG) console.log(`🔄 GET request to: ${endpoint}`);

    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.error(
        `❌ Error getting KV value for key "${key}". Status: ${response.status} ${response.statusText}`
      );

      try {
        const errorData = await response.json();
        console.error("Error details:", errorData);
      } catch (e) {
        console.error("Could not parse error response as JSON");
      }

      return null;
    }

    // Try to parse as JSON, but fall back to text if not valid JSON
    try {
      const data = await response.json();
      if (DEBUG)
        console.log(`✅ Successfully retrieved KV value for key "${key}"`);
      return data;
    } catch (e) {
      if (DEBUG)
        console.log(
          `⚠️ Retrieved non-JSON value for key "${key}", returning as text`
        );
      return (await response.text()) as unknown as T;
    }
  } catch (error) {
    console.error(`❌ Error getting KV value for key "${key}":`, error);
    return null;
  }
};

/**
 * Delete a key-value pair from Cloudflare KV storage
 */
export const deleteKVValue = async (key: string): Promise<boolean> => {
  if (!apiConfig) {
    console.error(
      "❌ Cloudflare API not configured. Call initCloudflareApi first."
    );
    throw new Error(
      "Cloudflare API not configured. Call initCloudflareApi first."
    );
  }

  if (DEBUG) console.log(`🔄 Deleting KV value for key "${key}"`);

  try {
    const { accountId, namespaceId, proxyUrl } = apiConfig;
    // Use our proxy instead of direct Cloudflare API
    const endpoint = `${proxyUrl}/api/cloudflare/accounts/${accountId}/storage/kv/namespaces/${namespaceId}/values/${key}`;

    if (DEBUG) console.log(`🔄 DELETE request to: ${endpoint}`);

    const response = await fetch(endpoint, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error(`❌ Error deleting KV value for key "${key}":`, errorData);
      console.error(`Status: ${response.status} ${response.statusText}`);
      return false;
    }

    if (DEBUG) console.log(`✅ Successfully deleted KV value for key "${key}"`);
    return true;
  } catch (error) {
    console.error(`❌ Error deleting KV value for key "${key}":`, error);
    return false;
  }
};

/**
 * List all keys in the KV namespace
 */
export const listKVKeys = async (): Promise<string[] | null> => {
  if (!apiConfig) {
    console.error(
      "❌ Cloudflare API not configured. Call initCloudflareApi first."
    );
    throw new Error(
      "Cloudflare API not configured. Call initCloudflareApi first."
    );
  }

  if (DEBUG) console.log("🔄 Listing all KV keys");

  try {
    const { accountId, namespaceId, proxyUrl } = apiConfig;
    // Use our proxy instead of direct Cloudflare API
    const endpoint = `${proxyUrl}/api/cloudflare/accounts/${accountId}/storage/kv/namespaces/${namespaceId}/keys`;

    if (DEBUG) console.log(`🔄 GET request to: ${endpoint}`);

    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.error(
        `❌ Error listing KV keys. Status: ${response.status} ${response.statusText}`
      );

      try {
        const errorData = await response.json();
        console.error("Error details:", errorData);
      } catch (e) {
        console.error("Could not parse error response as JSON");
      }

      return null;
    }

    const data: CloudflareKVResponse = await response.json();
    if (DEBUG) {
      console.log("✅ Successfully listed KV keys");
      console.log(`Found ${data.result.length} keys`);
    }

    return data.result.map((item: any) => item.name);
  } catch (error) {
    console.error("❌ Error listing KV keys:", error);
    return null;
  }
};
